// src/components/songEdit/hooks/useSongEditHandlers.ts
import { songEditService } from '../../../services/songEditService';
import { SongFormData } from '../types/songEdit';
import { toast } from 'react-toastify';

// Human-friendly labels for fields that show up in validation warnings.
const FIELD_LABELS: Record<string, string> = {
  video_link: 'Video link',
  copyright: 'Copyright',
  title: 'Title',
  language: 'Language',
  tags: 'Tags',
  composer: 'Composer',
  lyricist: 'Lyricist',
  tune: 'Tune'
};

const labelFor = (field: string): string => {
  const root = field.split('.')[0];
  return FIELD_LABELS[root] || root;
};

interface SongEditHandlersProps {
  setFormData: (data: SongFormData) => void;
  setShowSongForm: (show: boolean) => void;
  resetForms: () => void;
  handleSaveSuccess: () => void;
  loadSongData: (data: SongFormData) => void;
  isDirty: boolean;
  initialFormData: SongFormData;
}

export const useSongEditHandlers = ({
  setFormData,
  setShowSongForm,
  resetForms,
  handleSaveSuccess,
  loadSongData,
  isDirty,
  initialFormData
}: SongEditHandlersProps) => {
  
  const handleSongSelect = async (songId: number, versionId?: number) => {
    try {
      if (isDirty && !window.confirm('You have unsaved changes. Loading a new song will discard these changes. Continue?')) {
        return;
      }

      console.log('Fetching song data for ID:', songId);
      const songData = await songEditService.getSongById(songId);
      console.log('Raw song data:', songData);
      console.log('Raw lyricist object:', songData.lyricist);

      // The loader is lenient: it returns whatever is in the database, plus a
      // list of fields that don't meet the rules. Surface those to the user so
      // they can fix the song themselves.
      if (Array.isArray(songData._validationWarnings) && songData._validationWarnings.length > 0) {
        const fields = Array.from(new Set(
          songData._validationWarnings.map((w: { field: string }) => labelFor(w.field))
        ));
        toast.warning(
          `This song has data that needs attention: ${fields.join(', ')}. ` +
          `Please update and save.`,
          { autoClose: 8000 }
        );
      }
      
      const transformedData: SongFormData = {
        title: songData.title || '',
        composer: songData.composer?.full_name || songData.composer || '',
        lyricist: songData.lyricist?.full_name || songData.lyricist || '',
        primaryTune: songData.tune?.tune_name || songData.primary_tune || '',
        alternateTunes: songData.alternate_tunes || [],
        tune_ids: songData.tune_ids || [],
        tags: songData.tags || [],
        video_link: songData.video_link || '',
        copyright: songData.copyright || '',
        language: songData.language || 'en',
        song_id: songData.song_id,
        tune_id: songData.tune?.tune_id || undefined,
        tuneData: songData.tune || undefined
      };

      if (songData.composer && typeof songData.composer === 'object') {
        console.log('Processing composer data:', songData.composer);
        transformedData.composerData = {
          composer_id: songData.composer.composer_id,
          full_name: songData.composer.full_name,
          type: 'composer',
          era: songData.composer.era,
          origin: songData.composer.origin
        };
      }

      if (songData.lyricist && typeof songData.lyricist === 'object') {
        console.log('Processing lyricist data:', songData.lyricist);
        transformedData.lyricistData = {
          composer_id: songData.lyricist.lyricist_id, // Fix: use lyricist_id not composer_id
          full_name: songData.lyricist.full_name,
          type: 'lyricist',
          era: songData.lyricist.era,
          origin: songData.lyricist.origin
        };
      }

      console.log('Transformed data:', transformedData);
      loadSongData(transformedData);
    } catch (error) {
      console.error('Error loading song:', error);
      toast.error('Failed to load song data');
    }
  };

  const handleSave = async (formData: SongFormData): Promise<{ song_id: number; title: string } | null> => {
    console.log('[useSongEditHandlers] Starting save operation with data:', {
      song_id: formData.song_id,
      title: formData.title,
      composerData: formData.composerData,
      lyricistData: formData.lyricistData
    });

    const isNewSong = !formData.song_id;

    try {
      const savedSong = await songEditService.saveSong(formData, initialFormData);
      console.log('[useSongEditHandlers] Save successful, response:', savedSong);
      toast.success('Song saved successfully');

      // For new songs, return the song info so caller can show add file modal
      if (isNewSong && savedSong?.song?.song_id) {
        return {
          song_id: savedSong.song.song_id,
          title: formData.title
        };
      }

      handleSaveSuccess();
      return null;
    } catch (error: any) {
      console.error('[useSongEditHandlers] Save failed:', error);

      // Check if this is an ENTITY_EXISTS error
      if (error.response?.data?.error?.code === 'ENTITY_EXISTS') {
        // Re-throw with entity data so parent can handle it
        throw error.response.data.error;
      }

      toast.error('Failed to save song');
      return null;
    }
  };


  return {
    handleSongSelect,
    handleSave
  };
};